/**
 * Commissions Page
 *
 * Pipeline view of the artist's custom-commission projects.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Palette, Calendar, Inbox, DollarSign, Clock, CheckCircle, Download } from 'lucide-react'
import { useCommissions, useReconciliation, useCommissionsAccess } from '../hooks'
import { commissionsApi } from '../api/easelApi'
import { Commission, CommissionStatus } from '../types'

// =============================================================================
// Status + stage config
// =============================================================================

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

const statusConfig: Record<CommissionStatus, { label: string; variant: BadgeVariant }> = {
  [CommissionStatus.NEW]: { label: 'New', variant: 'default' },
  [CommissionStatus.SCOPING]: { label: 'Scoping', variant: 'default' },
  [CommissionStatus.QUOTED]: { label: 'Quoted', variant: 'secondary' },
  [CommissionStatus.DEPOSIT_DUE]: { label: 'Deposit Due', variant: 'secondary' },
  [CommissionStatus.DEPOSIT_PAID]: { label: 'Deposit Paid', variant: 'outline' },
  [CommissionStatus.IN_PROGRESS]: { label: 'In Progress', variant: 'outline' },
  [CommissionStatus.FINAL_DUE]: { label: 'Final Due', variant: 'secondary' },
  [CommissionStatus.FINAL_PAID]: { label: 'Final Paid', variant: 'outline' },
  [CommissionStatus.DELIVERED]: { label: 'Delivered', variant: 'outline' },
  [CommissionStatus.COMPLETED]: { label: 'Completed', variant: 'secondary' },
  [CommissionStatus.DECLINED]: { label: 'Declined', variant: 'destructive' },
  [CommissionStatus.CANCELLED]: { label: 'Cancelled', variant: 'destructive' },
  [CommissionStatus.REFUNDED]: { label: 'Refunded', variant: 'destructive' },
}

type Stage = 'all' | 'leads' | 'quoted' | 'active' | 'completed' | 'closed'

const STAGE_STATUSES: Record<Exclude<Stage, 'all'>, CommissionStatus[]> = {
  leads: [CommissionStatus.NEW, CommissionStatus.SCOPING],
  quoted: [CommissionStatus.QUOTED, CommissionStatus.DEPOSIT_DUE],
  active: [
    CommissionStatus.DEPOSIT_PAID,
    CommissionStatus.IN_PROGRESS,
    CommissionStatus.FINAL_DUE,
    CommissionStatus.FINAL_PAID,
    CommissionStatus.DELIVERED,
  ],
  completed: [CommissionStatus.COMPLETED],
  closed: [CommissionStatus.DECLINED, CommissionStatus.CANCELLED, CommissionStatus.REFUNDED],
}

function stageOf(status: CommissionStatus): Exclude<Stage, 'all'> {
  return (Object.keys(STAGE_STATUSES) as Array<Exclude<Stage, 'all'>>).find((s) =>
    STAGE_STATUSES[s].includes(status)
  ) as Exclude<Stage, 'all'>
}

function money(value?: number | string | null, currency = 'USD'): string {
  if (value === null || value === undefined || value === '') return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value))
}

// =============================================================================
// Commission card
// =============================================================================

function CommissionCard({ commission, onOpen }: { commission: Commission; onOpen: (c: Commission) => void }) {
  const config = statusConfig[commission.status]

  return (
    <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => onOpen(commission)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={config.variant}>{config.label}</Badge>
              <Badge variant="outline">{commission.source}</Badge>
              <span className="text-xs text-muted-foreground font-mono">{commission.reference}</span>
            </div>
            <h3 className="font-semibold line-clamp-1">{commission.title || 'Untitled commission'}</h3>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(commission.createdAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {money(commission.quoteAmount, commission.currency)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Page
// =============================================================================

export function CommissionsPage() {
  const navigate = useNavigate()
  const { data: access, isLoading: accessLoading } = useCommissionsAccess()
  const gated = !accessLoading && access ? !access.enabled : false

  const { data, isLoading } = useCommissions()
  const { data: recon } = useReconciliation()
  const [stage, setStage] = useState<Stage>('all')

  const downloadCsv = async () => {
    const blob = await commissionsApi.downloadReconciliationCsv()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'commissions-reconciliation.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const commissions = data?.data || []

  const counts = {
    leads: commissions.filter((c) => stageOf(c.status) === 'leads').length,
    quoted: commissions.filter((c) => stageOf(c.status) === 'quoted').length,
    active: commissions.filter((c) => stageOf(c.status) === 'active').length,
    completed: commissions.filter((c) => stageOf(c.status) === 'completed').length,
    closed: commissions.filter((c) => stageOf(c.status) === 'closed').length,
  }

  const filtered =
    stage === 'all' ? commissions : commissions.filter((c) => stageOf(c.status) === stage)

  const openCommission = (c: Commission) => navigate(`/app/easel/commissions/${c.id}`)

  const stageButton = (value: Stage, label: string, count: number) => (
    <Button
      variant={stage === value ? 'default' : 'outline'}
      size="sm"
      onClick={() => setStage(value)}
    >
      {label} ({count})
    </Button>
  )

  if (gated) {
    return (
      <div className="space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-semibold tracking-tight">Commissions</h1>
        </div>
        <Card className="p-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Palette className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">Commissions isn&apos;t enabled yet</h3>
          <p className="text-muted-foreground">
            This feature is in limited release. It&apos;ll appear here once it&apos;s turned on for your studio.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Commissions</h1>
            <p className="text-muted-foreground">Manage your custom-commission projects</p>
          </div>
        </div>
        {counts.leads > 0 && (
          <Badge variant="default" className="text-lg px-4 py-2">
            {counts.leads} New
          </Badge>
        )}
      </div>

      {/* Financials */}
      {recon && recon.count > 0 && (
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-6">
            <div>
              <p className="text-xs text-muted-foreground">Collected (USD)</p>
              <p className="text-lg font-semibold">{money(recon.totals.collectedUsd, 'USD')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Settled (GBP)</p>
              <p className="text-lg font-semibold">{money(recon.totals.settledGbp, 'GBP')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Platform take (USD)</p>
              <p className="text-lg font-semibold">{money(recon.totals.platformTakeUsd, 'USD')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Artist payout (GBP)</p>
              <p className="text-lg font-semibold">{money(recon.totals.artistPayoutGbp, 'GBP')}</p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto" onClick={downloadCsv}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={`cursor-pointer ${stage === 'leads' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStage('leads')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Inbox className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{counts.leads}</p>
              <p className="text-sm text-muted-foreground">Leads</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${stage === 'quoted' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStage('quoted')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{counts.quoted}</p>
              <p className="text-sm text-muted-foreground">Quoted</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${stage === 'active' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStage('active')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{counts.active}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${stage === 'completed' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStage('completed')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{counts.completed}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {stageButton('all', 'All', commissions.length)}
        {stageButton('leads', 'Leads', counts.leads)}
        {stageButton('quoted', 'Quoted', counts.quoted)}
        {stageButton('active', 'Active', counts.active)}
        {stageButton('completed', 'Completed', counts.completed)}
        {stageButton('closed', 'Closed', counts.closed)}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-6 w-2/3 mb-2" />
                <Skeleton className="h-4 w-1/3 mb-2" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Palette className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">
            {stage === 'all' ? 'No commissions yet' : `No ${stage} commissions`}
          </h3>
          <p className="text-muted-foreground">
            {stage === 'all'
              ? 'Promote a commission inquiry from your Contact page to start a project here.'
              : `You don't have any ${stage} commissions.`}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((commission) => (
            <CommissionCard key={commission.id} commission={commission} onOpen={openCommission} />
          ))}
        </div>
      )}
    </div>
  )
}
